import { useMemo } from "react";

import { ModuleManifest } from "../model";

/**
 * Represents a cached route for O(1) lookup.
 */
export interface CachedRoute {
  /** The module ID. */
  readonly id: string;

  /** Whether this module renders fullscreen. */
  readonly isFullscreen: boolean;

  /** The fullscreen paths if applicable. */
  readonly fullscreenPaths?: readonly string[];
}

/**
 * Hook that creates a memoized route cache from modules.
 * Provides O(1) route lookups instead of O(n) iteration.
 *
 * CACHE INVALIDATION: The cache is automatically rebuilt whenever the
 * `modules` array changes, ensuring stale data is never returned. This
 * includes hot reloads, dynamic module loading, and module updates.
 *
 * @param modules The available modules.
 * @returns A map of module ID to cached route info. Map is recreated
 *          whenever modules array changes, guaranteeing freshness.
 */
export const useRouteCache = (modules: readonly ModuleManifest[]): Map<string, CachedRoute> => {
  return useMemo(() => {
    const cache = new Map<string, CachedRoute>();

    for (const module of modules) {
      const { info, fullscreen } = module;
      const { id } = info;

      cache.set(id, {
        id,
        isFullscreen: fullscreen === true,
        fullscreenPaths: Array.isArray(fullscreen) ? fullscreen : undefined,
      });
    }

    return cache;
    /* ↓ Dependency ensures cache is rebuilt when modules change
       This prevents stale module metadata from being served */
  }, [modules]);
};

/**
 * Extracts the module ID from a pathname.
 *
 * @param pathname The URL pathname.
 * @param moduleRootPath The root path for modules (e.g., "/modules").
 * @returns The module ID, or null if not found.
 */
export const extractModuleIdFromPath = (pathname: string, moduleRootPath: string): string | null => {
  // Remove trailing slash for consistency
  let normalizedPath = pathname.endsWith("/") && pathname.length > 1 ? pathname.slice(0, -1) : pathname;

  // Remove module root path prefix
  if (normalizedPath.startsWith(moduleRootPath)) {
    normalizedPath = normalizedPath.slice(moduleRootPath.length);
  }

  // Extract module ID (first segment after root)
  const segments = normalizedPath.split("/").filter(Boolean);
  return segments.length > 0 ? segments[0] : null;
};

/**
 * Hook that validates if a pathname maps to a known module.
 * Enables early 404 detection without route matching.
 *
 * FRESHNESS: Always uses the current pathname and routeCache provided,
 * so validation is never stale. Returns false for unknown modules.
 *
 * @param pathname The current pathname (always fresh).
 * @param routeCache The cached routes (rebuilt on module changes).
 * @param moduleRootPath The root path for modules.
 * @returns Object with validation result and module ID.
 */
export const usePathValidation = (
  pathname: string,
  routeCache: Map<string, CachedRoute>,
  moduleRootPath: string,
): { isValid: boolean; moduleId: string | null } => {
  return useMemo(() => {
    const moduleId = extractModuleIdFromPath(pathname, moduleRootPath);

    // Root path is always valid
    if (!moduleId) {
      return { isValid: true, moduleId: null };
    }

    // Check if module exists in cache (which is always up-to-date)
    const isValid = routeCache.has(moduleId);
    return { isValid, moduleId };
  }, [pathname, routeCache, moduleRootPath]);
};

/**
 * Hook that determines if a pathname should render fullscreen.
 * Memoized for performance.
 *
 * Recomputes whenever pathname, routeCache, or moduleRootPath
 * changes. Never returns stale fullscreen state because it evaluates
 * current pathname against current route cache.
 *
 * @param pathname The current pathname (always fresh).
 * @param routeCache The cached routes (rebuilt on module changes).
 * @param moduleRootPath The root path for modules.
 * @returns true if fullscreen should be rendered (based on current state).
 */
export const useIsFullscreenRoute = (pathname: string, routeCache: Map<string, CachedRoute>, moduleRootPath: string): boolean => {
  return useMemo(() => {
    const moduleId = extractModuleIdFromPath(pathname, moduleRootPath);
    if (!moduleId) return false;

    const route = routeCache.get(moduleId);
    if (!route) return false;

    // If marked as fullscreen globally, return true
    if (route.isFullscreen) return true;

    // Check specific fullscreen paths
    if (route.fullscreenPaths) {
      const fullPath = pathname.endsWith("/") && pathname.length > 1 ? pathname.slice(0, -1) : pathname;

      return route.fullscreenPaths.some((path) => {
        // Simple path matching (can be enhanced with matchPath if needed)
        return fullPath.includes(path);
      });
    }

    return false;
    /* ↓ Dependencies ensure this is always fresh
       Recalculates on pathname, routeCache, or moduleRootPath change */
  }, [pathname, routeCache, moduleRootPath]);
};
